import React, { useState, useRef, useEffect } from "react";
import "./lendersQA.css";
import { useDispatch } from "react-redux";
import { toggleToast } from "../../Actions/ToggleToast";
import API_Root from "../../Constants/API_Root";
import { authFetch } from "../../Authentication/authService";

const LendersQA = ({ userEmail, selectedTeamId, filenames = [] }) => {
  const dispatch = useDispatch();

  const [questionFiles, setQuestionFiles] = useState([]);

  const [referenceFiles, setReferenceFiles] = useState([]);
  const [referenceMode, setReferenceMode] = useState(null); // upload | kb
  const [selectedKBFiles, setSelectedKBFiles] = useState([]);

  const [qaResults, setQaResults] = useState(null);

  const [sessions, setSessions] = useState([]);
  const [sessionId, setSessionId] = useState(null);

  const [chatInput, setChatInput] = useState("");
  const [chatHistory, setChatHistory] = useState([]);

  const [loading, setLoading] = useState(false);
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionLoading, setSessionLoading] = useState(false);

  const chatEndRef = useRef(null);

  // =========================
  // AUTO SCROLL
  // =========================
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatHistory, chatLoading]);

  // =========================
  // FETCH SESSIONS
  // =========================
  const fetchSessions = async () => {
    if (!selectedTeamId) return;

    try {
      const res = await authFetch(`${API_Root}chat/fetch-lenders-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ team_id: selectedTeamId }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message);

      let sessionList = [];

      if (Array.isArray(data)) sessionList = data;
      else if (Array.isArray(data?.sessions)) sessionList = data.sessions;
      else if (Array.isArray(data?.data?.sessions))
        sessionList = data.data.sessions;

      setSessions(sessionList);
    } catch (err) {
      dispatch(toggleToast({ show: true, type: "error", message: err.message }));
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [selectedTeamId]);

  // =========================
  // FETCH SESSION MESSAGES
  // =========================
  const fetchSessionMessages = async (id) => {
    setSessionLoading(true);
    setSessionId(id);

    try {
      const res = await authFetch(`${API_Root}chat/fetch-session-messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: id }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message);

      const formatted = (data?.messages || []).map((msg) => ({
        type: msg.role === "user" ? "user" : "bot",
        text: msg.content,
      }));

      setChatHistory(formatted);
    } catch (err) {
      dispatch(toggleToast({ show: true, type: "error", message: err.message }));
    } finally {
      setSessionLoading(false);
    }
  };

  // =========================
  // GENERATE
  // =========================
  const handleGenerate = async () => {
    if (questionFiles.length === 0) {
      return dispatch(
        toggleToast({
          show: true,
          type: "error",
          message: "Upload question file",
        })
      );
    }

    // if (!referenceMode) {
    //   return dispatch(
    //     toggleToast({
    //       show: true,
    //       type: "error",
    //       message: "Select reference source",
    //     })
    //   );
    // }

    if (referenceMode === "upload" && referenceFiles.length === 0) {
      return dispatch(
        toggleToast({
          show: true,
          type: "error",
          message: "Upload reference file",
        })
      );
    }

    if (referenceMode === "kb" && selectedKBFiles.length === 0) {
      return dispatch(
        toggleToast({
          show: true,
          type: "error",
          message: "Select a KB file",
        })
      );
    }

    setLoading(true);

    try {
      const formData = new FormData();

      questionFiles.forEach((file) => {
        formData.append("question_files", file);
      });
      
      formData.append("email", userEmail);
      
      if (referenceMode === "upload") {
        referenceFiles.forEach((file) => {
          formData.append("reference_files", file);
        });
      } else {
        selectedKBFiles.forEach((file) => {
          formData.append("reference_files", file);
        });
      }

      const res = await authFetch(`${API_Root}chat/generate-answers`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.message);

      setQaResults(data);

      // Excel download
      try {
        const excelRes = await authFetch(`${API_Root}chat/generate-excel`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            content: data?.results || data,
          }),
        });
      
        if (excelRes.ok) {
          const blob = await excelRes.blob();
      
          // ✅ Get filename from backend
          const contentDisposition =
            excelRes.headers.get("Content-Disposition");
      
          let filename = "Lenders_QA_Report.xlsx";
      
          if (contentDisposition) {
            const match = contentDisposition.match(
              /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
            );
      
            if (match && match[1]) {
              filename = match[1].replace(/['"]/g, "");
            }
          }
      
          const url = window.URL.createObjectURL(blob);
      
          const a = document.createElement("a");
          a.href = url;
          a.download = filename;
      
          document.body.appendChild(a);
          a.click();
      
          a.remove();
          window.URL.revokeObjectURL(url);
        }
      } catch (err) {
        console.error(err);
      }

      // Create session
      const sessionRes = await authFetch(`${API_Root}chat/create-lenders-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_name: `Session ${new Date()}`,
          team_id: selectedTeamId,
        }),
      });

      const sessionData = await sessionRes.json();

      setSessionId(sessionData.session_id);
      setChatHistory([]);

      fetchSessions();

      dispatch(
        toggleToast({
          show: true,
          type: "success",
          message: "Session created & Excel downloaded",
        })
      );
    } catch (err) {
      dispatch(toggleToast({ show: true, type: "error", message: err.message }));
    } finally {
      setLoading(false);
    }
  };

  // =========================
  // CHAT
  // =========================
  const handleSendMessage = async () => {
    if (!chatInput.trim()) return;

    const userMessage = chatInput;

    setChatHistory((prev) => [...prev, { type: "user", text: userMessage }]);
    setChatInput("");
    setChatLoading(true);

    try {
      const res = await authFetch(`${API_Root}chat/chat-with-docs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message: userMessage,
          qa_results: qaResults?.results || qaResults,
          session_id: sessionId,
          email: userEmail,
        }),
      });

      const data = await res.json();

      setChatHistory((prev) => [
        ...prev,
        { type: "bot", text: data?.response || data?.content || "No response" },
      ]);
    } catch (err) {
      dispatch(toggleToast({ show: true, type: "error", message: err.message }));
    } finally {
      setChatLoading(false);
    }
  };

  return (
    <div className="lqa-wrapper">
  
      {/* 🔥 TOP SESSION BAR */}
      <div className="lqa-topbar">
        {sessions.length === 0 && <span>No Sessions</span>}
  
        {sessions.map((s, i) => (
          <div
            key={s.id}
            className={`lqa-session-pill ${sessionId === s.id ? "active" : ""}`}
            onClick={() => fetchSessionMessages(s.id)}
          >
            {s.session_name || `Session ${i + 1}`}
          </div>
        ))}
      </div>
  
      {/* MAIN */}
      <div className="lqa-main">
  
        {/* INSTRUCTIONS */}
        <div className="lqa-card lqa-info">
          <h3>How to use</h3>
          <ul>
            <li>Upload your Question file</li>
            <li>Select ONE reference source</li>
            <li>Generate answers</li>
            <li>Chat with results</li>
          </ul>
        </div>
  
        {/* UPLOAD */}
        <div className="lqa-card lqa-upload">
          <h3>Lender’s Q&A</h3>
  
          <label>Question File</label>
          <input
  type="file"
  multiple
  onChange={(e) => setQuestionFiles(Array.from(e.target.files))}
/>

{questionFiles.length > 0 && (
  <div className="lqa-file-name">
    {questionFiles.map((file) => (
      <div key={file.name}>{file.name}</div>
    ))}
  </div>
)}
  
          <div className="lqa-radio-group">
            <label><b>Reference Source</b></label>
  
            <div className="lqa-radio">
              <input
                type="radio"
                checked={referenceMode === "upload"}
                onChange={() => {
                  setReferenceMode("upload");
                  setSelectedKBFiles([]);
                }}
              />
              Upload File
            </div>
  
            {referenceMode === "upload" && (
              <>
              <input
  type="file"
  multiple
  onChange={(e) => setReferenceFiles(Array.from(e.target.files))}
/>

{referenceFiles.length > 0 && (
  <div className="lqa-file-name">
    {referenceFiles.map((file) => (
      <div key={file.name}>{file.name}</div>
    ))}
  </div>
)}
              </>
            )}
  
            <div className="lqa-radio">
              <input
                type="radio"
                checked={referenceMode === "kb"}
                onChange={() => {
                  setReferenceMode("kb");
                  setReferenceFiles([]);
                }}
              />
              Select from KB
            </div>
  
            {referenceMode === "kb" && (
  <div className="lqa-kb-files">
    {filenames.map((f, i) => (
      <label key={i} className="lqa-kb-item">
        <input
          type="checkbox"
          checked={selectedKBFiles.includes(f.filename)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedKBFiles((prev) => [...prev, f.filename]);
            } else {
              setSelectedKBFiles((prev) =>
                prev.filter((item) => item !== f.filename)
              );
            }
          }}
        />
        {f.filename}
      </label>
    ))}
  </div>
)}
          </div>
  
          <button className="lqa-btn" onClick={handleGenerate}>
            {loading ? "Processing..." : "Generate"}
          </button>
        </div>
  
        {/* CHAT */}
        {sessionId && (
          <div className="lqa-card lqa-chat-card">
            <h3>Chat</h3>
  
            <div className="lqa-chat-box">
              {chatHistory.map((msg, i) => (
                <div key={i} className={`lqa-msg ${msg.type}`}>
                  {msg.text}
                </div>
              ))}
  
              {chatLoading && <div className="lqa-msg bot">Typing...</div>}
            </div>
  
            <div className="lqa-chat-input">
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSendMessage()}
                placeholder="Ask something..."
              />
              <button onClick={handleSendMessage}>Send</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LendersQA;